// =============================================================================
// FILE:
// src/modules/pms/task_material/TaskMaterialPlanningPage.js
// =============================================================================

import React, { useEffect, useState } from "react";

import WBSTree from "../components/WBSTree";

import TaskMaterialGrid from "./components/TaskMaterialGrid";

import MaterialAllocationDrawer from "./components/MaterialAllocationDrawer";

import {
  getWBSTree
} from "../services/wbsService";

import {
  getTaskMaterialPlan
} from "../services/taskMaterialService";


// =============================================================================
// COMPONENT
// =============================================================================

export default function TaskMaterialPlanningPage({

  projectId

}) {

  // ===========================================================================
  // STATE
  // ===========================================================================
  const [tree, setTree] = useState([]);

  const [loading, setLoading] = useState(false);

  const [selectedNode, setSelectedNode] = useState(null);

  const [materialRows, setMaterialRows] = useState([]);

  const [materialLoading, setMaterialLoading] = useState(false);

  const [allocationDrawerOpen, setAllocationDrawerOpen] =
    useState(false);

  // ===========================================================================
  // LOAD WBS TREE
  // ===========================================================================
  const loadTree = async () => {

    try {

      setLoading(true);

      const res = await getWBSTree(projectId);

      setTree(res.data.data || []);

    } catch (err) {

      console.error(
        "Error loading WBS tree",
        err
      );

    } finally {

      setLoading(false);
    }
  };

  // ===========================================================================
  // LOAD TASK MATERIALS
  // ===========================================================================
  const loadTaskMaterials = async (taskId) => {

    try {

      setMaterialLoading(true);

      const res = await getTaskMaterialPlan(taskId);

      setMaterialRows(
        res.data.data || []
      );

    } catch (err) {

      console.error(
        "Error loading task materials",
        err
      );

      setMaterialRows([]);

    } finally {

      setMaterialLoading(false);
    }
  };

  // ===========================================================================
  // INITIAL LOAD
  // ===========================================================================
  useEffect(() => {

    if (projectId) {
      loadTree();
    }

  }, [projectId]);

  // ===========================================================================
  // NODE SELECT
  // ===========================================================================
  const handleSelectNode = async (node) => {

    setSelectedNode(node);

    // -------------------------------------------------------------------------
    // ONLY TASK SUPPORTS MATERIALS
    // -------------------------------------------------------------------------
    if (node?.type !== "TASK") {

      setMaterialRows([]);

      return;
    }

    await loadTaskMaterials(node.id);
  };

  // ===========================================================================
  // REFRESH MATERIALS
  // ===========================================================================
  const refreshMaterials = async () => {

    if (
      selectedNode &&
      selectedNode.type === "TASK"
    ) {
      await loadTaskMaterials(
        selectedNode.id
      );
    }
  };

  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (

    <div style={styles.container}>

      {/* ===================================================================== */}
      {/* LEFT PANEL */}
      {/* ===================================================================== */}
      <div style={styles.leftPanel}>

        <div style={styles.panelHeader}>
          Project WBS
        </div>

        <div style={styles.treeContainer}>

          {loading ? (

            <div style={styles.loader}>
              Loading WBS...
            </div>

          ) : (

            <WBSTree

              data={tree}

              selectedNode={selectedNode}

              onSelect={handleSelectNode}

              // ---------------------------------------------------------------
              // DISABLED ACTIONS
              // ---------------------------------------------------------------
              onAdd={() => {}}
              onEdit={() => {}}
              onDelete={() => {}}
              onDependency={() => {}}

              predecessorMap={{}}
              successorMap={{}}
            />
          )}

        </div>

      </div>

      {/* ===================================================================== */}
      {/* RIGHT PANEL */}
      {/* ===================================================================== */}
      <div style={styles.rightPanel}>

        {/* ------------------------------------------------------------------- */}
        {/* HEADER */}
        {/* ------------------------------------------------------------------- */}
        <div style={styles.materialHeader}>

          <div>

            <div style={styles.materialTitle}>
              Task Material Planning
            </div>

            <div style={styles.materialSubText}>

              {selectedNode
                ? (
                    <>
                      Selected:
                      {" "}
                      <b>{selectedNode.name}</b>
                      {" "}
                      ({selectedNode.type})
                    </>
                  )
                : "Select a TASK from WBS"}

            </div>

          </div>

          {/* ----------------------------------------------------------------- */}
          {/* ACTIONS */}
          {/* ----------------------------------------------------------------- */}
          <div style={styles.headerActions}>

            <button
              style={styles.refreshBtn}
              onClick={refreshMaterials}
            >
              🔄 Refresh
            </button>

            <button
              style={{
                ...styles.allocateBtn,

                opacity:
                  selectedNode?.type === "TASK"
                    ? 1
                    : 0.5
              }}
              disabled={
                selectedNode?.type !== "TASK"
              }
              onClick={() =>
                setAllocationDrawerOpen(true)
              }
            >
              + Allocate BOM
            </button>

          </div>

        </div>

        {/* ------------------------------------------------------------------- */}
        {/* GRID */}
        {/* ------------------------------------------------------------------- */}
        <div style={styles.gridContainer}>

          {!selectedNode ? (

            <div style={styles.emptyState}>
              Select a TASK node to manage materials
            </div>

          ) : selectedNode.type !== "TASK" ? (

            <div style={styles.emptyState}>
              Materials can only be managed for TASK nodes
            </div>

          ) : (

            <TaskMaterialGrid
              rows={materialRows}
              loading={materialLoading}
              onRefresh={refreshMaterials}
            />

          )}

        </div>

      </div>

      {/* ===================================================================== */}
      {/* ALLOCATION DRAWER */}
      {/* ===================================================================== */}
      <MaterialAllocationDrawer

        open={allocationDrawerOpen}

        onClose={() =>
          setAllocationDrawerOpen(false)
        }

        taskNode={selectedNode}

        onSuccess={refreshMaterials}

      />

    </div>
  );
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  container: {
    display: "flex",
    gap: 15,
    height: "78vh"
  },

  // ===========================================================================
  // LEFT PANEL
  // ===========================================================================
  leftPanel: {
    width: "38%",
    border: "1px solid #ddd",
    borderRadius: 8,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  },

  panelHeader: {
    padding: "12px 14px",
    borderBottom: "1px solid #eee",
    fontWeight: 600,
    background: "#f9fafb"
  },

  treeContainer: {
    flex: 1,
    overflow: "hidden"
  },

  // ===========================================================================
  // RIGHT PANEL
  // ===========================================================================
  rightPanel: {
    flex: 1,
    border: "1px solid #ddd",
    borderRadius: 8,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  },

  materialHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px",
    borderBottom: "1px solid #eee"
  },

  materialTitle: {
    fontSize: 18,
    fontWeight: 600
  },

  materialSubText: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 4
  },

  headerActions: {
    display: "flex",
    gap: 10
  },

  refreshBtn: {
    padding: "8px 12px",
    border: "none",
    borderRadius: 6,
    background: "#e5e7eb",
    cursor: "pointer"
  },

  allocateBtn: {
    padding: "8px 14px",
    border: "none",
    borderRadius: 6,
    background: "#4f46e5",
    color: "#fff",
    cursor: "pointer"
  },

  gridContainer: {
    flex: 1,
    overflow: "hidden"
  },

  loader: {
    padding: 20,
    textAlign: "center"
  },

  emptyState: {
    padding: 30,
    textAlign: "center",
    color: "#6b7280"
  }
};